import React, { useEffect, useState, useRef } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import interactionPlugin from '@fullcalendar/interaction';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PlaceIcon from '@mui/icons-material/Place';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SportsHockeyIcon from '@mui/icons-material/SportsHockey';
import EventIcon from '@mui/icons-material/Event';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ConstructionIcon from '@mui/icons-material/Construction'; // Ice Cut Icon
import IceSkatingIcon from '@mui/icons-material/IceSkating';
import { toZonedTime } from 'date-fns-tz'; // ✅ Correct in v3.2

import {
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  ButtonGroup,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  TextField,
  IconButton,
  Stack,
  Typography,
  Divider,
  Menu,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { parseISO, parse, format } from 'date-fns';
import FilterListIcon from '@mui/icons-material/FilterList';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import TodayIcon from '@mui/icons-material/Today';
import '@fullcalendar/common/main.css';
import CloseIcon from '@mui/icons-material/Close';

const API_RESERVATIONS_URL =
  'https://us-central1-aotw-arena.cloudfunctions.net/api/calendar/417/443';

// // Sport mapping
// const sportMapping = {
//   10: "Hockey",
//   53: "Ice Skating",
//   // 57:"",
//   // 58: "",
//   999: "Other",
// };
// Sport mapping with icons
const sportMapping = {
  10: {
    id: 10,
    name: 'Hockey',
    icon: <SportsHockeyIcon fontSize="small" color="primary" />,
  },
  53: {
    id: 53,
    name: 'Ice Skating',
    icon: <IceSkatingIcon fontSize="small" color="primary" />,
  },
  999: {
    id: 999,
    name: 'Other',
    icon: <EventIcon fontSize="small" color="primary" />,
  },
};
// Simple debounce hook.
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
}

// Memoized filters component.
const FiltersContent = React.memo(
  ({
    selectedResource,
    setSelectedResource,
    selectedCategories,
    setSelectedCategories,
    selectedSports,
    setSelectedSports,
    setFilterModalOpen,
    searchTerm,
    setSearchTerm,
    resources,
    allCategories,
    isMobile,
  }) => (
    <div
      style={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        gap: '8px',
        // marginRight: "25px",
      }}
    >
      <FormControl variant="outlined" size="small" fullWidth>
        <InputLabel>Rink</InputLabel>
        <Select
          label="Rink"
          value={selectedResource}
          onChange={(e) => setSelectedResource(e.target.value)}
        >
          <MenuItem value="all">All Rinks</MenuItem>
          {resources.map((r) => (
            <MenuItem key={r.id} value={r.id}>
              {r.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl variant="outlined" size="small" fullWidth>
        <InputLabel>Event Categories</InputLabel>
        <Select
          label="Event Categories"
          multiple
          value={selectedCategories}
          onChange={(e) => setSelectedCategories(e.target.value)}
          renderValue={(selected) => selected.join(', ')}
        >
          {allCategories.map((cat) => (
            <MenuItem key={cat} value={cat}>
              <Checkbox checked={selectedCategories.indexOf(cat) > -1} />
              <ListItemText primary={cat.charAt(0).toUpperCase() + cat.slice(1)} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl variant="outlined" size="small" fullWidth>
        <InputLabel>Sports</InputLabel>
        <Select
          label="Sports"
          multiple
          value={selectedSports}
          onChange={(e) => setSelectedSports(e.target.value)}
          renderValue={(selected) =>
            selected
              .map((id) => sportMapping[id]?.name) // Ensure it displays the sport name
              .join(', ')
          }
        >
          {Object.entries(sportMapping).map(([id, { name, icon }]) => (
            <MenuItem key={id} value={id}>
              <Checkbox checked={selectedSports.includes(id)} />
              {/* {icon} Display the corresponding icon */}
              <ListItemText primary={name} sx={{ marginLeft: '8px' }} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <TextField
        variant="outlined"
        size="small"
        label="Search"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        fullWidth
      />

      <Button
        variant="outlined"
        onClick={() => {
          setSelectedResource('all');
          setSelectedCategories([]);
          setSelectedSports([]);
          setSearchTerm('');
        }}
      >
        Reset
      </Button>
      {isMobile && (
        <Button
          onClick={() => {
            setFilterModalOpen(false);
          }}
          variant="outlined"
        >
          Apply
        </Button>
      )}
    </div>
  ),
);
// const LearnToSkateNotification = ({ onClose }) => (
//   <Alert
//     severity="info"
//     action={
//       <IconButton color="inherit" size="small" onClick={onClose}>
//         <CloseIcon fontSize="inherit" />
//       </IconButton>
//     }
//     sx={{ fontWeight: 'bold' }}
//   >
//     ⛸️ <b>REGISTRATION FOR LEARN TO SKATE IS NOW OPEN!</b>{' '}
//     <i>
//       From preschool to adults, from complete beginners to hockey power skating. Skate rentals
//       included in all classes.
//     </i>{' '}
//     Late registrations accepted. Register by clicking{' '}
//     <a href="https://bondsports.co/activity/programs/CO_ED-adult-ICE_SKATING/10315">here</a>.
//   </Alert>
// );

// const SummerDevelopmentNotification = ({ onClose }) => (
//   <Alert
//     severity="info"
//     action={
//       <IconButton color="inherit" size="small" onClick={onClose}>
//         <CloseIcon fontSize="inherit" />
//       </IconButton>
//     }
//     sx={{ fontWeight: 'bold' }}
//   >
//     🎉 Save $55 — registration fee waived for May Learn to Skate!  Click{' '}
//     <a href="https://learntoskate.rink.guide/">here</a> to
//     register.
//   </Alert>
// );

/**
 * CalendarComponent is a React functional component that renders a dual-calendar interface
 * for managing and displaying events. It supports various views (day, week, month, list),
 * filtering, and event customization.
 *
 * @component
 *
 * @returns {JSX.Element} The rendered CalendarComponent.
 *
 * @description
 * - This component uses FullCalendar for rendering events and provides a dual-calendar view
 *   for "Community Rink" and "Main Rink" when in day view and all resources are selected.
 * - It includes filtering options for resources, categories, sports, and search terms.
 * - Events are styled dynamically based on their properties, including color codes and icons.
 * - The component supports mobile and desktop layouts with responsive controls.
 * - Events are fetched from an API based on the selected date range and filters.
 *
 * @example
 * <CalendarComponent />
 *
 * @dependencies
 * - React
 * - FullCalendar (dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin)
 * - Material-UI components (Button, Dialog, Typography, etc.)
 * - date-fns for date formatting and manipulation
 *
 * @state
 * @property {string} selectedResource - The currently selected resource ID or "all".
 * @property {Array<string>} selectedCategories - The list of selected event categories.
 * @property {Array<string>} selectedSports - The list of selected sports IDs.
 * @property {string} searchTerm - The current search term for filtering events.
 * @property {Array<Object>} resources - The list of available resources.
 * @property {Array<string>} allCategories - The list of all available categories.
 * @property {Array<Object>} events - The list of events to display.
 * @property {string} view - The current calendar view (e.g., "timeGridDay", "listWeek").
 * @property {Object|null} selectedEvent - The currently selected event for the dialog.
 * @property {boolean} filterModalOpen - Whether the filter modal is open.
 * @property {Date} currentDate - The current date in the calendar.
 * @property {Object} currentRange - The current date range with `start` and `end` properties.
 * @property {HTMLElement|null} anchorEl - The anchor element for the dropdown menu.
 *
 * @methods
 * @function handleDropdownOpen - Opens the dropdown menu for view selection.
 * @function handleDropdownClose - Closes the dropdown menu and changes the view if specified.
 * @function renderEventContent - Customizes the rendering of events in the calendar.
 * @function extractCategories - Extracts categories from event data, prioritizing default categories.
 * @function mapColorCode - Maps a color code ID to a corresponding color.
 * @function handleEventMount - Applies custom styles to events when they are mounted.
 * @function formatDuration - Formats a duration in minutes into a human-readable string.
 * @function fetchResourcesAndEvents - Fetches resources and events from the API based on the date range.
 * @function handleDatesSet - Updates the current date range when the calendar view changes.
 * @function renderListEventContent - Customizes the rendering of events in list view.
 * @function gotoNext - Navigates to the next date range in the calendar.
 * @function gotoPrev - Navigates to the previous date range in the calendar.
 * @function gotoToday - Navigates to the current date in the calendar.
 * @function getIceCutTime - Retrieves the ice cut time for an event, if available.
 * @function handleEventClick - Handles clicks on events, opening a dialog with event details.
 * @function renderControls - Renders the calendar controls (e.g., navigation, filters).
 *
 * @hooks
 * - useState: Manages component state for various properties.
 * - useEffect: Handles side effects, such as fetching data and syncing scroll positions.
 * - useRef: Provides references to DOM elements and FullCalendar instances.
 * - useDebounce: Debounces the search term input for better performance.
 * - useMediaQuery: Detects whether the user is on a mobile device.
 * - useTheme: Accesses the current Material-UI theme.
 */
export default function CalendarComponent() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [selectedResource, setSelectedResource] = useState('all');
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedSports, setSelectedSports] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const [resources, setResources] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [events, setEvents] = useState([]);
  const [view, setView] = useState('timeGridDay'); // default to day view
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [currentRange, setCurrentRange] = useState({
    start: new Date(),
    end: new Date(),
  });

  const [anchorEl, setAnchorEl] = useState(null);
  const isDropdownOpen = Boolean(anchorEl);
  const leftCalendarScrollRef = useRef(null);
  const rightCalendarScrollRef = useRef(null);

  const handleDropdownOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleDropdownClose = (viewName) => {
    setAnchorEl(null);
    if (viewName) {
      setView(viewName);
      leftCalendarRef.current?.getApi()?.changeView(viewName);
      rightCalendarRef.current?.getApi()?.changeView(viewName); // Sync both calendars
    }
  };

  // Refs for dual calendars.
  const leftCalendarRef = useRef(null);
  const rightCalendarRef = useRef(null);
  const titleCategoryMapping = {
    'Public Skate': ['public skate', 'public skating'],
    'Members Only': ['members only'],
    'Stick and Puck': ['stick & puck', 'stick and puck'],
    'Learn to Skate': ['learn to skate', 'lts'],
    'Learn to Play': ['learn to play', 'ltp', 'altp'],
    'Freestyle': ['freestyle'],
    'Aspire': ['aspire'],
    '8U Hockey': ['8u'],
    '10U Hockey': ['10u'],
    '12U Hockey': ['12u'],
    '14U Hockey': ['14u'],
    '16U Hockey': ['16u'],
    '18U Hockey': ['18u'],
  };
  /**
   * Renders the content of a calendar event with a custom layout.
   *
   * @param {Object} eventInfo - Information about the event to render.
   * @param {Object} eventInfo.event - The event object containing details about the event.
   * @param {Object} eventInfo.event.extendedProps - Additional properties of the event.
   * @param {Array<number>} [eventInfo.event.extendedProps.sportIds] - Array of sport IDs associated with the event.
   * @param {number} [eventInfo.event.extendedProps.colorCodeId] - ID representing the color code of the event.
   * @param {string} eventInfo.event.title - The title of the event.
   * @param {string|Date} eventInfo.event.start - The start time of the event.
   * @param {string|Date} eventInfo.event.end - The end time of the event.
   *
   * @returns {JSX.Element} A JSX element representing the styled event content.
   */
  const renderEventContent = (eventInfo) => {
    const sportIds = eventInfo.event.extendedProps.sportIds || [];
    const eventColor = mapColorCode(eventInfo.event.extendedProps.colorCodeId); // Get event color

    // Format event time
    const startTime = format(new Date(eventInfo.event.start), 'h:mm a');
    const endTime = format(new Date(eventInfo.event.end), 'h:mm a');

    // Get the first sport icon
    const sportIcon = sportIds.length > 0 ? sportMapping[sportIds[0]]?.icon : null;

    return (
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '24px auto', // First column for icon, second for text
          alignItems: 'center',
          gap: '8px',
          width: '100%',
          // padding: '6px 8px'
          overflow: 'hidden', // Prevent overflow
          height: '100%', // Ensure full height usage
        }}
      >
        {/* Icon Column (kept centered) */}
        {!isMobile && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {sportIcon &&
              React.cloneElement(sportIcon, { fontSize: 'small', style: { color: eventColor } })}
          </div>
        )}

        {/* Time (top-aligned) and Event Title */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            // justifyContent: 'space-between', // Pushes time up
            // alignSelf: 'flex-start', // Moves time to top
            height: '100%', // Ensures full height alignment
          }}
        >
          {/* Time at the top */}
          <span
            style={{
              fontSize: '0.8rem',
              fontWeight: 'bold',
              // color: eventColor,
              whiteSpace: 'nowrap',
              alignSelf: 'flex-start', // Forces time to stay at the top
            }}
          >
            {startTime} - {endTime}
          </span>

          {/* Event Title (kept below) */}
          <b
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              alignSelf: 'flex-start',
              // marginTop: '2px', // Adds a little space between time and title
            }}
          >
            {eventInfo.event.title}
          </b>
        </div>
      </div>
    );
  };

  // Extract categories from event data while keeping default categories at the top
  const extractCategories = (data) => {
    const defaultCategories = new Set(Object.keys(titleCategoryMapping)); // Default categories
    const additionalCategories = new Set(); // Extra categories from event titles

    data.forEach((res) => {
      res.slots.forEach((slot) => {
        const slotTitle = slot.title.toLowerCase().trim();

        // Check if the title matches any predefined category
        let matched = false;
        Object.entries(titleCategoryMapping).forEach(([category, keywords]) => {
          if (keywords.some((keyword) => slotTitle.includes(keyword))) {
            defaultCategories.add(category); // Ensure default categories stay
            matched = true;
          }
        });

        // If no predefined category matched, add as an additional category
        if (!matched) {
          additionalCategories.add(slotTitle);
        }
      });
    });

    // Merge default categories first, followed by additional categories
    return [...defaultCategories, ...[...additionalCategories].sort()];
  };
  const mapColorCode = (colorCodeId) => {
    const colorMap = {
      13512: '#d9534f', // Stick and Puck
      13522: '#6b9bd0', // Blue
      13517: '#5cb85c', // Youth Hockey
      13507: '#ff00ff', // Freestyle
      13500: '#FFAD00', // Public Skating
      13528: '#800080', // Aspire
      13506: '#ff00ff', // Freestyle
      13501: '#fa771e', // Light Orange
      13519: '#228b22', // Silvertips
    };
    return colorMap[colorCodeId] || '#007bff';
  };

  const handleEventMount = (info) => {
    const baseColor = mapColorCode(info.event.extendedProps.colorCodeId);
    if (!baseColor) return;

    // Function to blend colors toward a neutral background
    const blendWithWhite = (hex, percent) => {
      const num = parseInt(hex.slice(1), 16);
      const r = Math.round((num >> 16) * (1 - percent) + 255 * percent);
      const g = Math.round(((num >> 8) & 255) * (1 - percent) + 255 * percent);
      const b = Math.round((num & 255) * (1 - percent) + 255 * percent);
      return `rgb(${r}, ${g}, ${b})`;
    };

    // Function to determine if text should be white or black for contrast
    const getTextColor = (hex) => {
      const num = parseInt(hex.slice(1), 16);
      const r = (num >> 16) & 255;
      const g = (num >> 8) & 255;
      const b = num & 255;
      const brightness = (r * 299 + g * 587 + b * 114) / 1000;
      return brightness > 140 ? '#000' : '#474747'; // If bright, use dark text; if dark, use white text
    };

    const backgroundColor = blendWithWhite(baseColor, 0.8); // **Blend with white for a softer look**
    const borderColor = baseColor; // darkenColor(baseColor, 0.3); // **Keep the border darker**
    const textColor = getTextColor(baseColor); // **Ensure readable text color**

    // Apply styles
    info.el.style.setProperty('--fc-event-bg-color', backgroundColor, 'important');
    // Apply styles for list view
    if (view.toLowerCase().includes('list')) {
      info.el.style.setProperty('--fc-event-border-color', borderColor, 'important');
    } else {
      info.el.style.setProperty('--fc-event-border-color', 'none', 'important');
    }

    info.el.style.setProperty('--fc-event-text-color', textColor, 'important');

    // Style updates
    // info.el.style.backgroundColor = backgroundColor;
    info.el.style.fontFamily = 'Arial, sans-serif';
    info.el.style.color = textColor;
    info.el.style.borderLeft = `8px solid ${borderColor}`; // Darker left border
    info.el.style.borderRadius = '4px';
    info.el.style.padding = '6px';
    info.el.style.fontWeight = 'bold';
  };

  /**
   * Formats a duration given in minutes into a human-readable string.
   *
   * @param {number} minutes - The duration in minutes to format.
   * @returns {string} A formatted string representing the duration in hours and minutes.
   *                   For example, "90" minutes will be formatted as "1 hr 30 min".
   */
  const formatDuration = (minutes) => {
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return remainingMinutes === 0 ? `${hours} hr` : `${hours} hr ${remainingMinutes} min`;
  };

  /**
   * Fetches resources and events within a specified date range, processes the data,
   * and updates the state with resources, categories, and filtered events.
   *
   * @async
   * @function fetchResourcesAndEvents
   * @param {Date} startDate - The start date of the range to fetch events for.
   * @param {Date} endDate - The end date of the range to fetch events for.
   * @returns {Promise<void>} Resolves when the resources and events are fetched and processed.
   *
   * @throws {Error} Logs an error if the fetch request fails or the API returns an error.
   *
   * @description
   * - Fetches data from the API using the provided date range.
   * - Extracts resources and updates the `resources` state.
   * - Dynamically generates a list of categories based on event titles and predefined mappings.
   * - Filters and maps events based on selected resources, categories, sports, and search terms.
   * - Updates the `events` state with the filtered and processed events.
   *
   * @example
   * // Example usage:
   * const startDate = new Date('2023-01-01');
   * const endDate = new Date('2023-01-31');
   * await fetchResourcesAndEvents(startDate, endDate);
   */
  const fetchResourcesAndEvents = async (startDate, endDate) => {
    if (!startDate || !endDate) {
      console.error('Invalid date range:', startDate, endDate);
      return;
    }
    const startStr = startDate.toLocaleDateString('en-CA', {
      timeZone: 'America/Los_Angeles',
    });
    const endStr = endDate.toLocaleDateString('en-CA', {
      timeZone: 'America/Los_Angeles',
    });

    const apiUrl = `${API_RESERVATIONS_URL}?startDate=${startStr}&endDate=${endStr}`;

    try {
      const resp = await fetch(apiUrl);
      const data = await resp.json();
      if (data.err) {
        console.error('API error:', data.err);
        return;
      }
      const extractedResources = data.map((r) => ({
        id: r.id,
        name: r.name,
      }));
      setResources(extractedResources);

      // Create a Set of predefined category names (e.g., "8U", "LTS", etc.)
      const predefinedCategories = new Set(Object.keys(titleCategoryMapping));

      // Flatten the mapping into a Set of keywords for filtering event titles
      const defaultCategoryKeywords = new Set(
        Object.values(titleCategoryMapping)
          .flat()
          .map((c) => c.toLowerCase()),
      );

      // Initialize a Set to store the categories (start with predefined categories)
      const catSet = new Set(predefinedCategories);

      // Iterate over event data to extract categories
      data.forEach((res) => {
        res.slots.forEach((slot) => {
          const title = slot.title.toLowerCase().trim();

          // Skip if the title contains any of the default category keywords
          if (![...defaultCategoryKeywords].some((keyword) => title.includes(keyword))) {
            // Add event title as a category if it's not a default category
            catSet.add(title);
          }
        });
      });

      // Ensure predefined categories appear first and sort the rest alphabetically
      const sortedCategories = [
        ...predefinedCategories, // Add predefined categories at the top
        ...[...catSet].filter((cat) => !predefinedCategories.has(cat)).sort(), // Add dynamic categories and sort them
      ];

      setAllCategories(sortedCategories); // Update the list of categories

      const mappedEvents = data.flatMap((res) => {
        if (selectedResource === 'all' || +res.id === +selectedResource) {
          return res.slots.map((slot) => ({
            id: slot.id,
            title: slot.title,
            start: slot.startDate.split('T')[0] + 'T' + slot.startTime,
            end: slot.endDate.split('T')[0] + 'T' + slot.endTime,
            extendedProps: {
              resourceName: res.name,
              reservationId: slot.reservationId,
              slotType: slot.slotType,
              category: slot.title.toLowerCase().trim(),
              sportIds: slot.sportIds,
              publicNotes: slot.publicNotes,
              colorCodeId: slot.colorCodeId, // 🔹 Pass colorCodeId here
              children: slot.children,
            },
          }));
        }
        return [];
      });

      // Apply filtering logic
      const finalEvents = mappedEvents
        .filter((evt) => {
          if (!selectedCategories.length) return true;

          // Match the event's category against selected category names
          return selectedCategories.some((category) =>
            titleCategoryMapping[category]?.some((keyword) =>
              evt.extendedProps.category.includes(keyword),
            ),
          );
        })
        .filter((evt) => {
          if (!selectedSports.length) return true;
          return (
            evt.extendedProps.sportIds &&
            evt.extendedProps.sportIds.some((id) => selectedSports.includes(String(id)))
          );
        })
        .filter((evt) => {
          if (!debouncedSearchTerm.trim()) return true;
          return evt.title.toLowerCase().includes(debouncedSearchTerm.toLowerCase());
        });

      setEvents(finalEvents);
    } catch (error) {
      console.error('Fetch events error:', error);
    }
  };
  useEffect(() => {
    const leftCalendar = leftCalendarRef.current?.getApi();
    const rightCalendar = rightCalendarRef.current?.getApi();

    if (!leftCalendar || !rightCalendar) return;

    setTimeout(() => {
      const leftScrollContainer = leftCalendar.el?.querySelector('.fc-timegrid-body');
      const rightScrollContainer = rightCalendar.el?.querySelector('.fc-timegrid-body');

      if (!leftScrollContainer || !rightScrollContainer) return;

      let isSyncingLeft = false;
      let isSyncingRight = false;

      const handleLeftScroll = () => {
        if (isSyncingRight) return;
        isSyncingLeft = true;
        rightScrollContainer.scrollTop = leftScrollContainer.scrollTop;
        isSyncingLeft = false;
      };

      const handleRightScroll = () => {
        if (isSyncingLeft) return;
        isSyncingRight = true;
        leftScrollContainer.scrollTop = rightScrollContainer.scrollTop;
        isSyncingRight = false;
      };

      leftScrollContainer.addEventListener('scroll', handleLeftScroll);
      rightScrollContainer.addEventListener('scroll', handleRightScroll);

      // Observer to check for changes in the DOM
      const observer = new MutationObserver(() => {
        const newLeftScroll = leftCalendar.el?.querySelector('.fc-timegrid-body');
        const newRightScroll = rightCalendar.el?.querySelector('.fc-timegrid-body');

        if (newLeftScroll && newRightScroll) {
          leftScrollContainer.removeEventListener('scroll', handleLeftScroll);
          rightScrollContainer.removeEventListener('scroll', handleRightScroll);

          newLeftScroll.addEventListener('scroll', handleLeftScroll);
          newRightScroll.addEventListener('scroll', handleRightScroll);
        }
      });

      observer.observe(leftCalendar.el, { childList: true, subtree: true });
      observer.observe(rightCalendar.el, { childList: true, subtree: true });

      return () => {
        leftScrollContainer.removeEventListener('scroll', handleLeftScroll);
        rightScrollContainer.removeEventListener('scroll', handleRightScroll);
        observer.disconnect();
      };
    }, 500);
  }, []);

  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return; // Skip the first call on load
    }
    if (currentRange.start && currentRange.end) {
      fetchResourcesAndEvents(currentRange.start, currentRange.end);
    } else {
      fetchResourcesAndEvents(currentDate, currentDate);
    }

    // Set up an interval to re-fetch events every 5 minutes
    const interval = setInterval(
      () => {
        if (currentRange.start && currentRange.end) {
          fetchResourcesAndEvents(currentRange.start, currentRange.end);
        } else {
          fetchResourcesAndEvents(currentDate, currentDate);
        }
      },
      5 * 60 * 1000,
    );

    return () => clearInterval(interval);
  }, [
    selectedResource,
    selectedCategories,
    selectedSports,
    debouncedSearchTerm,
    view,
    currentDate,
    currentRange,
  ]);

  /**
   * Handles the setting of date ranges for the calendar view.
   *
   * @param {Object} arg - The argument object containing details about the current view and date range.
   * @param {Object} arg.view - The current view object.
   * @param {Date} arg.view.currentStart - The start date of the current view range.
   * @param {Date} arg.view.currentEnd - The end date of the current view range.
   * @param {string} arg.view.type - The type of the current view (e.g., "list", "month").
   * @param {Date} [arg.start] - The fallback start date if `currentStart` is not provided.
   * @param {Date} [arg.end] - The fallback end date if `currentEnd` is not provided.
   *
   * Ensures that list views do not start in the past and updates the current date range
   * only if it has changed.
   */
  const handleDatesSet = (arg) => {
    let rangeStart = arg.view.currentStart || arg.start;
    let rangeEnd = arg.view.currentEnd || arg.end;

    // Ensure list views don't start in the past
    if (arg.view.type.includes('list') && rangeStart < new Date()) {
      rangeStart = new Date(); // Set to today
    }

    // Force update only if the range changes
    if (
      !currentRange.start ||
      !currentRange.end ||
      currentRange.start.getTime() !== rangeStart.getTime() ||
      currentRange.end.getTime() !== rangeEnd.getTime()
    ) {
      setCurrentDate(rangeStart);
      setCurrentRange({ start: new Date(rangeStart), end: new Date(rangeEnd) });
    }
  };

  // Custom event content for list view: append resource name.
  /**
   * Generates custom HTML content for rendering a list event in the calendar.
   *
   * @param {Object} arg - The argument object containing event details.
   * @param {Object} arg.event - The event object.
   * @param {string} arg.event.title - The title of the event.
   * @param {Object} arg.event.extendedProps - Additional properties of the event.
   * @param {string} [arg.event.extendedProps.resourceName] - The name of the resource associated with the event.
   * @param {string} arg.event.startStr - The start time of the event in ISO string format.
   * @param {string} arg.event.endStr - The end time of the event in ISO string format.
   * @returns {Object} An object containing the generated HTML content for the event.
   * @returns {string} .html - The HTML string representing the event content.
   */
  const renderListEventContent = (arg) => {
    const resourceName = arg.event.extendedProps.resourceName || '';
    const startTime = format(parseISO(arg.event.startStr), 'h:mma');
    const endTime = format(parseISO(arg.event.endStr), 'h:mma');
    return {
      html: `
      <b>${arg.event.title}</b>
      <span style="margin-left:8px;font-size:smaller;">(${resourceName})</span>
      <span style="margin-left:8px;font-size:smaller;">${startTime} - ${endTime}</span>`,
    };
  };
  const formatDateTime = (dateString, timeString) => {
    const dateTime = parseISO(dateString.replace('Z', ''));
    return format(dateTime, 'EEE, MMM dd, h:mmaaa');
  };

  const gotoNext = () => {
    if (leftCalendarRef.current) {
      const leftApi = leftCalendarRef.current.getApi();
      leftApi.next();
      const newDate = leftApi.getDate();
      if (rightCalendarRef.current) {
        rightCalendarRef.current.getApi().gotoDate(newDate);
      }
    }
  };

  const gotoPrev = () => {
    if (leftCalendarRef.current) {
      const leftApi = leftCalendarRef.current.getApi();
      leftApi.prev();
      const newDate = leftApi.getDate();
      if (rightCalendarRef.current) {
        rightCalendarRef.current.getApi().gotoDate(newDate);
      }
    }
  };

  const gotoToday = () => {
    if (leftCalendarRef.current) {
      const leftApi = leftCalendarRef.current.getApi();
      leftApi.today();
      const newDate = leftApi.getDate();
      if (rightCalendarRef.current) {
        rightCalendarRef.current.getApi().gotoDate(newDate);
      }
    }
  };

  /**
   * Extracts and formats the ice cut time from an event object.
   *
   * @param {Object} event - The event object containing details about the event.
   * @param {Array} [event.children] - An array of child events associated with the main event.
   * @param {string} [event.timezone] - The timezone of the event. Defaults to 'America/Los_Angeles' if not provided.
   * @returns {Object|null} An object containing the formatted start and end times of the ice cut event, or null if no ice cut event is found.
   * @returns {string} return.startTime - The formatted start time of the ice cut event (e.g., '3:00 PM').
   * @returns {string} return.endTime - The formatted end time of the ice cut event (e.g., '3:15 PM').
   */
  const getIceCutTime = (event) => {
    if (!event?.children || event.children.length === 0) return null;

    const iceCutEvent = event.children.find((child) => child.slotType === 'maintenance');

    if (!iceCutEvent) return null;

    const timeZone = event.timezone || 'America/Los_Angeles'; // Default to Pacific Time

    // Convert times to local format
    const formattedStartTime = format(
      toZonedTime(parse(iceCutEvent.startTime, 'HH:mm:ss', new Date()), timeZone),
      'h:mm a',
    );

    // Convert Ice Cut End Time
    const formattedEndTime = format(
      toZonedTime(parse(iceCutEvent.endTime, 'HH:mm:ss', new Date()), timeZone),
      'h:mm a',
    );

    return {
      startTime: formattedStartTime,
      endTime: formattedEndTime,
    };
  };

  const handleEventClick = ({ event }) => {
    if (!event.extendedProps.reservationId) {
      console.log('No reservationId, not opening dialog.');
      return;
    }

    const iceCutTime = getIceCutTime(event.extendedProps);

    setSelectedEvent({
      title: event.title,
      startDate: event.startStr,
      endDate: event.endStr,
      resource: {
        name: event.extendedProps.resourceName,
        notes: event.extendedProps.publicNotes,
        colorCodeId: event.extendedProps.colorCodeId,
        sportIds: event.extendedProps.sportIds,
        children: event.extendedProps.children,
      },
      notes: event.extendedProps.publicNotes,
      colorCodeId: event.extendedProps.colorCodeId,
      sportIds: event.extendedProps.sportIds,
      children: event.extendedProps.children,
      iceCutTime, // Include calculated Ice Cut Time
    });
  };

  const isListView = view.toLowerCase().includes('list');

  // Split events for dual columns.
  const eventsCommunity = events.filter(
    (evt) =>
      evt.extendedProps.resourceName &&
      evt.extendedProps.resourceName.toLowerCase().includes('community'),
  );
  const eventsMain = events.filter(
    (evt) =>
      evt.extendedProps.resourceName &&
      evt.extendedProps.resourceName.toLowerCase().includes('main'),
  );

  const renderControls = () => (
    <div style={{ padding: '8px' }}>
      {isMobile && (
        <div style={{ marginLeft: 'auto', marginRight: 'auto' }}>
          <b>{format(currentDate, 'MMMMMMM yyyy')}</b>
        </div>
      )}
      {!isMobile && (
        <>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
              marginBottom: '8px',
              // marginRight: "25px",
            }}
          >
            <Button variant="outlined" onClick={gotoPrev}>
              Prev
            </Button>
            <Button variant="outlined" onClick={gotoToday}>
              Today
            </Button>
            <Button variant="outlined" onClick={gotoNext}>
              Next
            </Button>

            <div style={{ marginLeft: 'auto', marginRight: 'auto' }}>
              <b>{format(currentDate, 'EEEE, MMM d, yyyy')}</b>
            </div>

            <Button
              variant="outlined"
              onClick={() => {
                setView('dayGridMonth');
                leftCalendarRef.current.getApi().changeView('dayGridMonth');
              }}
            >
              Month
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                setView('timeGridWeek');
                leftCalendarRef.current.getApi().changeView('timeGridWeek');
              }}
            >
              Week
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                setView('timeGridDay');
                leftCalendarRef.current.getApi().changeView('timeGridDay');
              }}
            >
              Day
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                setView('listWeek');
                leftCalendarRef.current.getApi().changeView('listWeek');
              }}
            >
              List
            </Button>
          </div>
        </>
      )}

      {isMobile ? (
        <>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '8px',
            }}
          >
            <Button
              variant="outlined"
              sx={{ borderColor: '#333', color: '#333' }}
              onClick={handleDropdownOpen}
              endIcon={<ArrowDropDownIcon />}
            >
              View
            </Button>

            <Menu
              anchorEl={anchorEl}
              open={isDropdownOpen}
              onClose={() => handleDropdownClose(null)}
            >
              <MenuItem onClick={() => handleDropdownClose('dayGridMonth')}>Month</MenuItem>
              <MenuItem onClick={() => handleDropdownClose('timeGridWeek')}>Week</MenuItem>
              <MenuItem onClick={() => handleDropdownClose('timeGridDay')}>Day</MenuItem>
              <MenuItem onClick={() => handleDropdownClose('listWeek')}>List</MenuItem>
            </Menu>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <ButtonGroup variant="outlined" sx={{ borderColor: '#333' }}>
                <Button onClick={gotoPrev} sx={{ borderColor: '#333', color: '#333' }}>
                  <ArrowBackIcon />
                </Button>

                <Button
                  onClick={gotoToday}
                  sx={{ borderColor: '#333', color: '#333' }}
                  startIcon={<TodayIcon />}
                >
                  Today
                </Button>

                <Button onClick={gotoNext} sx={{ borderColor: '#333', color: '#333' }}>
                  <ArrowForwardIcon />
                </Button>
              </ButtonGroup>
            </div>

            <Button
              variant="outlined"
              sx={{ borderColor: '#333', color: '#333' }}
              onClick={() => setFilterModalOpen(true)}
            >
              <FilterListIcon />
            </Button>

            <Dialog open={filterModalOpen} onClose={() => setFilterModalOpen(false)} fullWidth>
              <DialogTitle>Filters</DialogTitle>
              <DialogContent>
                <FiltersContent
                  selectedResource={selectedResource}
                  setSelectedResource={setSelectedResource}
                  selectedCategories={selectedCategories}
                  setSelectedCategories={setSelectedCategories}
                  selectedSports={selectedSports}
                  setSelectedSports={setSelectedSports}
                  searchTerm={searchTerm}
                  setSearchTerm={setSearchTerm}
                  resources={resources}
                  allCategories={allCategories}
                  isMobile={isMobile}
                  setFilterModalOpen={setFilterModalOpen}
                />
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setFilterModalOpen(false)} color="primary">
                  Close
                </Button>
              </DialogActions>
            </Dialog>
          </div>
        </>
      ) : (
        <FiltersContent
          selectedResource={selectedResource}
          setSelectedResource={setSelectedResource}
          selectedCategories={selectedCategories}
          setSelectedCategories={setSelectedCategories}
          selectedSports={selectedSports}
          setSelectedSports={setSelectedSports}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          resources={resources}
          allCategories={allCategories}
          isMobile={isMobile}
          setFilterModalOpen={setFilterModalOpen}
        />
      )}
    </div>
  );

  return (
    <div
      style={{
        width: 'calc(100vw - 10px)',
        height: '100vh',
        overflow: 'hidden',
      }}
    >
      {/* {showLearnToSkateNotification && (
        <LearnToSkateNotification onClose={() => setShowLearnToSkateNotification(false)} />
      )} */}
      {/* {showSummerDevelopmentNotification && (
        <SummerDevelopmentNotification
          onClose={() => setShowSummerDevelopmentNotification(false)}
        />
      )} */}
      {renderControls()}
      {view === 'timeGridDay' && selectedResource === 'all' ? (
        <div
          style={{
            display: 'flex',
            gap: '16px',
            padding: '8px',
            height: 'calc(100vh - 200px)',
            // marginRight: "25px",
          }}
        >
          <div style={{ flex: 1, height: '100%' }}>
            <h3 style={{ textAlign: 'center', fontFamily: 'Arial, sans-serif' }}>Community Rink</h3>
            <FullCalendar
              ref={leftCalendarRef}
              plugins={[dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin]}
              initialView="timeGridDay"
              headerToolbar={false}
              allDaySlot={false}
              slotMinTime="06:00:00"
              slotMaxTime="23:59:00"
              nowIndicator
              scrollTime={`${new Date().getHours().toString().padStart(2, '0')}:${new Date()
                .getMinutes()
                .toString()
                .padStart(2, '0')}:00`}
              height="100%"
              events={eventsCommunity}
              datesSet={handleDatesSet}
              eventClick={handleEventClick}
              eventDidMount={handleEventMount}
              eventContent={renderEventContent} // ✅ Add this to display icons in event title
            />
          </div>
          <div style={{ flex: 1, height: '100%', position: 'relative' }} className="right-calendar">
            {/* Dummy header to align with left calendar */}
            <h3 style={{ textAlign: 'center', fontFamily: 'Arial, sans-serif' }}>Main Rink</h3>
            <FullCalendar
              ref={rightCalendarRef}
              plugins={[dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin]}
              initialView="timeGridDay"
              headerToolbar={false}
              allDaySlot={false}
              slotMinTime="06:00:00"
              slotMaxTime="23:59:00"
              nowIndicator
              scrollTime={`${new Date().getHours().toString().padStart(2, '0')}:${new Date()
                .getMinutes()
                .toString()
                .padStart(2, '0')}:00`}
              height="100%"
              events={eventsMain}
              eventClick={handleEventClick}
              eventDidMount={handleEventMount}
              eventContent={renderEventContent} // ✅ Add this to display icons in event title
            />
          </div>
        </div>
      ) : (
        <div style={{ height: 'calc(100vh - 200px)', padding: '8px' }}>
          <FullCalendar
            ref={leftCalendarRef}
            plugins={[dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin]}
            initialView={view}
            headerToolbar={false}
            allDaySlot={false}
            slotMinTime="06:00:00"
            slotMaxTime="23:59:00"
            nowIndicator
            scrollTime={`${new Date().getHours().toString().padStart(2, '0')}:${new Date()
              .getMinutes()
              .toString()
              .padStart(2, '0')}:00`}
            height="100%"
            events={events}
            datesSet={handleDatesSet}
            eventClick={handleEventClick}
            eventDidMount={handleEventMount}
            displayEventTime={false}
            eventContent={isListView ? renderListEventContent : undefined}
          />
        </div>
      )}
      <Dialog open={!!selectedEvent} onClose={() => setSelectedEvent(null)} maxWidth="sm" fullWidth>
        {/* Event Title */}
        <DialogTitle sx={{ fontWeight: 'bold', fontSize: '1.5rem', color: '#1f3a5f' }}>
          {selectedEvent?.title}
        </DialogTitle>
        <Divider sx={{ marginY: '5px' }} />
        <DialogContent sx={{ padding: '10px' }}>
          {/* Date & Time */}
          <Typography
            variant="body1"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontWeight: 500,
            }}
          >
            <AccessTimeIcon fontSize="small" color="primary" />
            {selectedEvent?.startDate && selectedEvent?.endDate ? (
              <>
                {new Date(selectedEvent.startDate).toLocaleString('en-US', {
                  weekday: 'short',
                  month: 'short',
                  day: 'numeric',
                })}{' '}
                {new Date(selectedEvent.startDate).toLocaleTimeString('en-US', {
                  hour: '2-digit',
                  minute: '2-digit',
                })}{' '}
                -{' '}
                {new Date(selectedEvent.endDate).toLocaleTimeString('en-US', {
                  hour: '2-digit',
                  minute: '2-digit',
                })}
                &nbsp;(
                {formatDuration(
                  Math.round(
                    (new Date(selectedEvent.endDate) - new Date(selectedEvent.startDate)) / 60000,
                  ),
                )}
                )
              </>
            ) : (
              'No time available'
            )}
          </Typography>

          {/* Location */}
          <Typography
            variant="body1"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginTop: '8px',
            }}
          >
            <PlaceIcon fontSize="small" color="primary" />
            {selectedEvent?.resource?.name || 'Unknown location'}
          </Typography>

          {/* Additional Notes */}
          {selectedEvent?.resource?.notes && (
            <Typography
              variant="body2"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginTop: '8px',
                color: '#6b7c93',
              }}
            >
              <InfoOutlinedIcon fontSize="small" />
              {selectedEvent.resource?.notes}
            </Typography>
          )}

          {/* Sport Type with Icons */}
          {selectedEvent?.resource?.sportIds?.length > 0 && (
            <Typography
              variant="body2"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginTop: '12px',
                fontWeight: 600,
              }}
            >
              {selectedEvent.resource.sportIds.map(
                (id) =>
                  sportMapping[id]?.icon || (
                    <EventIcon key={sportMapping[id]?.name} fontSize="small" color="primary" />
                  ),
              )}
              {selectedEvent.resource.sportIds
                .map((id) => sportMapping[id]?.name || 'Other')
                .join(', ')}
            </Typography>
          )}

          {/* Ice Cut Details */}
          {selectedEvent?.iceCutTime && (
            <>
              <Divider sx={{ marginY: '12px' }} />
              <Typography
                variant="body2"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontWeight: 600,
                }}
              >
                <ConstructionIcon fontSize="small" color="primary" />
                Ice Cut: {selectedEvent.iceCutTime.startTime} - {selectedEvent.iceCutTime.endTime}
              </Typography>
            </>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setSelectedEvent(null)} color="primary" variant="outlined">
            Close
          </Button>
        </DialogActions>
      </Dialog>
      <style>{`
        @media (max-width: 600px) {
          .fc-header-toolbar {
            flex-direction: column;
            align-items: center;
          }
          .fc-toolbar-chunk {
            margin-bottom: 4px;
            text-align: center;
          }
        }

      `}</style>
    </div>
  );
}
